import { CheckIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { formatFullRange, telHref } from './format';
import { CopyIcon } from './icons';
import {
  Card,
  ContactName,
  ContactNumber,
  ContactPhoneRow,
  ContactShift,
  CopiedTip,
  CopyButton,
  Dot,
  NoNumber,
  POPOVER_CSS,
  RIGHT_ALIGNED_CSS,
  SrOnly,
  STACKED_POPOVER_CSS,
} from './OpsOnDutyBar.styles';
import { useReturnFocus } from './useReturnFocus';

import Popover from '@/components/Popover';
import type { OnDutyEntry } from '@/services/opsRota/types';
import { trackOpsNumberCopied, trackOpsNumberDialed } from '@/util/vexo';
import type { OpsBarSurface } from '@/util/vexo';

// Room kept between a card and the screen's right edge.
const EDGE_GAP = 16;

// UI spec §4.2: the "Copied" confirmation shows for 1.5 s (DB-12: "brief").
export const COPIED_MS = 1500;

// DB-15: on touch devices the number is also a tel: link.
const isTouch = () =>
  typeof window !== 'undefined' &&
  !!window.matchMedia?.('(pointer: coarse)')?.matches;

// Clipboard fallback: select the number so it can be copied by hand.
const selectText = (el: HTMLElement | null) => {
  if (!el) return;
  const range = document.createRange();
  range.selectNodeContents(el);
  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
};

type Props = {
  entry: OnDutyEntry;
  onClose: () => void;
  /**
   * Opened from the "+N more" list, over that list's card: offset
   * further and with a stronger shadow, so it reads as its own layer.
   */
  stacked?: boolean;
  // The bar's analytics dimension; defaults for direct use in tests.
  surface?: OpsBarSurface;
  /** Show the shift in this zone, not the browser's. */
  timeZone?: string;
  /** Where focus goes when the card closes with focus inside. */
  returnFocusTo?: () => HTMLElement | null;
  /** Where it goes instead when that button left with the card. */
  focusFallback?: () => HTMLElement | null;
};

// DB-10..DB-16. Esc and outside clicks close it (built into Popover).
const OpsContactPopover = ({
  entry,
  onClose,
  stacked = false,
  surface = 'dashboard',
  timeZone,
  returnFocusTo = () => null,
  focusFallback,
}: Props) => {
  const [copied, setCopied] = useState(false);
  const [alignRight, setAlignRight] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const numberRef = useRef<HTMLSpanElement>(null);
  useReturnFocus(cardRef, returnFocusTo, focusFallback);
  const timer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(timer.current), []);

  useLayoutEffect(() => {
    const right = cardRef.current?.getBoundingClientRect().right ?? 0;
    if (right > window.innerWidth - EDGE_GAP) setAlignRight(true);
  }, []);

  const copy = async () => {
    if (!entry.phone) return;
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard API unavailable');
      }
      await navigator.clipboard.writeText(entry.phone);
      trackOpsNumberCopied({ surface, method: 'clipboard' });
      setCopied(true);
      // Restarted on every click, as in the prototype.
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), COPIED_MS);
    } catch {
      selectText(numberRef.current);
      trackOpsNumberCopied({ surface, method: 'manual_select' });
    }
  };

  return (
    <Popover
      id={`opsContact-${entry.id}`}
      isOpen
      onRequestClose={onClose}
      css={{
        ...(stacked ? STACKED_POPOVER_CSS : POPOVER_CSS),
        ...(alignRight ? RIGHT_ALIGNED_CSS : {}),
      }}
    >
      <Card
        ref={cardRef}
        layer={stacked ? 'stacked' : undefined}
        data-layer={stacked ? 'stacked' : undefined}
        data-align={alignRight ? 'right' : undefined}
        role="dialog"
        aria-label={`Contact for ${entry.full_name}`}
        data-testid="opsContactPopover"
      >
        <ContactName data-testid="opsContactName">
          {entry.full_name}
          <Dot on={entry.on_duty} size="md" aria-hidden="true" />
        </ContactName>
        <ContactShift data-testid="opsContactShift">
          {formatFullRange(entry.starts_at, entry.ends_at, timeZone)}
        </ContactShift>
        {entry.phone ? (
          <ContactPhoneRow>
            <ContactNumber>
              <PhoneIcon aria-hidden="true" />
              <span ref={numberRef} data-testid="opsContactPhone">
                {isTouch() ? (
                  <a
                    href={telHref(entry.phone)}
                    onClick={() => trackOpsNumberDialed({ surface })}
                  >
                    {entry.phone}
                  </a>
                ) : (
                  entry.phone
                )}
              </span>
            </ContactNumber>
            <CopyButton
              type="button"
              title="Copy number"
              aria-label={`Copy ${entry.full_name}'s number`}
              data-testid="opsContactCopyButton"
              onClick={copy}
            >
              {copied ? (
                <CheckIcon
                  aria-hidden="true"
                  width={18}
                  height={18}
                  strokeWidth={2}
                />
              ) : (
                <CopyIcon width={20} height={20} />
              )}
              <CopiedTip
                shown={copied}
                aria-hidden="true"
                data-testid="opsContactCopiedTip"
                data-shown={copied}
              >
                Copied
              </CopiedTip>
            </CopyButton>
            <SrOnly role="status">{copied ? 'Copied' : ''}</SrOnly>
          </ContactPhoneRow>
        ) : (
          <NoNumber data-testid="opsContactNoNumber">
            No number on file
          </NoNumber>
        )}
      </Card>
    </Popover>
  );
};

export default OpsContactPopover;
